import { calculateShiftSelection } from "./calculateShiftSelection"
import { RowSelectionKind, TableAction, TableState } from "./types"

export interface TableReducerConfig<T> {
  extractRowKey: (rowData: T) => string
  rowSelectionKind?: RowSelectionKind
}

export function createTableReducer<T>({
  extractRowKey,
  rowSelectionKind,
}: TableReducerConfig<T>) {
  return function reducer(
    state: TableState<T>,
    action: TableAction<T>
  ): TableState<T> {
    switch (action.type) {
      case "toggle_group_expanded": {
        const expandedGroups = new Set(state.expandedGroups)
        if (expandedGroups.has(action.groupTitle)) {
          expandedGroups.delete(action.groupTitle)
        } else {
          expandedGroups.add(action.groupTitle)
        }
        return {
          ...state,
          expandedGroups: expandedGroups,
        }
      }
      case "toggle_row_selected": {
        let selected = new Map(state.selectedByKey)
        const rowKey = extractRowKey(action.rowData)
        if (rowSelectionKind === "single-select") {
          selected = new Map().set(rowKey, action.rowData)
        } else if (selected.has(rowKey)) {
          selected.delete(rowKey)
        } else if (state.isAllSelected) {
          selected = new Map(
            action.visibleRowData
              .filter((rowData) => extractRowKey(rowData) !== rowKey)
              .map((rowData) => [extractRowKey(rowData), rowData])
          )
        } else {
          selected.set(rowKey, action.rowData)
        }
        return {
          ...state,
          lastClickedRowKey: rowKey,
          selectedByKey: selected,
          isAllSelected: false,
        }
      }
      case "toggle_row_selected_with_shift": {
        const rows = calculateShiftSelection(
          action.rowData,
          action.visibleRowData,
          state.selectedByKey,
          state.lastClickedRowKey,
          extractRowKey
        )
        return {
          ...state,
          lastClickedRowKey: extractRowKey(action.rowData),
          selectedByKey: rows,
          isAllSelected: false,
        }
      }
      case "toggle_all_selected": {
        return {
          ...state,
          selectedByKey: new Map(),
          isAllSelected: !state.isAllSelected,
        }
      }
      case "clear_selected": {
        return {
          ...state,
          lastClickedRowKey: "",
          selectedByKey: new Map(),
          isAllSelected: false,
        }
      }
      case "select_rows": {
        return {
          ...state,
          lastClickedRowKey: "",
          selectedByKey: new Map(
            action.rowData.map((rowData) => [extractRowKey(rowData), rowData])
          ),
          isAllSelected: false,
        }
      }
      default: {
        return state
      }
    }
  }
}
